"use client";

import { useCallback, useEffect, useRef, useState, useActionState } from "react";
import { useTranslations } from "next-intl";
import { Chats, CheckCircle, PaperPlaneTilt, X } from "@phosphor-icons/react/dist/ssr";
import { submitFeedbackAction } from "@/lib/actions/feedback";
import type { FeedbackState } from "@/lib/actions/feedback";
import { useFocusTrap } from "@/lib/hooks/use-focus-trap";
import { useReducedMotion } from "@/lib/hooks/use-reduced-motion";
import { MOBILE_MQ } from "@/lib/breakpoints";
import { createClient } from "@/lib/supabase/client";
import { BusyLabel } from "@/components/ui/Spinner";

type Category = "Bug" | "Feature" | "General";
type WidgetState = "idle" | "open" | "success";

const CATEGORIES: Category[] = ["Bug", "Feature", "General"];

// Self-contained: reads the signed-in user's email client-side and renders
// nothing for anonymous visitors. Keeps the ROOT layout free of any server
// session read, so pages stay statically cacheable under cacheComponents.
export default function FeedbackWidget() {
  const t = useTranslations("feedback");
  const [email, setEmail] = useState<string | null>(null);
  const [widgetState, setWidgetState] = useState<WidgetState>("idle");
  const [category, setCategory] = useState<Category>("General");
  const [hasMessage, setHasMessage] = useState(false);
  const [shown, setShown] = useState(true);
  const [near, setNear] = useState(false);
  // True while a `#hero` section is intersecting the viewport AND we're on
  // a mobile breakpoint. The trigger tab competes for attention with the
  // primary CTA on small screens, so it stays parked off-canvas until the
  // user scrolls past the hero. Non-homepage routes have no `#hero`, the
  // observer never fires, and the widget behaves normally.
  const [hideOverHero, setHideOverHero] = useState(false);
  const firstFieldRef = useRef<HTMLTextAreaElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const lastY = useRef(0);
  const shownRef = useRef(true);
  const reducedMotion = useReducedMotion();

  const [actionState, formAction, isPending] = useActionState<FeedbackState, FormData>(
    submitFeedbackAction,
    undefined,
  );

  // Switch to success view when the action succeeds
  useEffect(() => {
    if (actionState?.status === "success") {
      setWidgetState("success");
    }
  }, [actionState]);

  // Read the signed-in user's email client-side; the widget renders nothing
  // for anon visitors (see the early return below).
  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data }) => setEmail(data.user?.email ?? null));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setEmail(session?.user?.email ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Detect pointer proximity to right edge
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      setNear(e.clientX >= window.innerWidth - 80);
    };
    const onLeave = () => setNear(false);
    document.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseleave", onLeave);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  // Hide while any `[data-feedback-hide-zone]` element is in the viewport
  // on mobile (<sm). Scoped to the mobile MQ so desktop's right rail never
  // collides with the tab. Both the MQ change and the IO entry feed the
  // same boolean. The data-attribute (not `#hero`) is the explicit contract
  // — semantic ids like "hero" risk colliding with unrelated sections on
  // other routes once the widget mounts globally.
  // Re-run the zone scan when the DOM gains/loses zones: the widget mounts once
  // in the root layout, so a zone rendered AFTER a client-side navigation (the
  // /chat composer) would otherwise never be observed. A debounced
  // MutationObserver is used rather than usePathname() on purpose — usePathname
  // in a layout-mounted client component makes every [slug] route dynamic.
  const [zoneEpoch, setZoneEpoch] = useState(0);
  useEffect(() => {
    let timer: number | null = null;
    const mo = new MutationObserver((muts) => {
      if (!muts.some((m) => m.type === "childList")) return;
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => setZoneEpoch((n) => n + 1), 200);
    });
    mo.observe(document.body, { childList: true, subtree: true });
    return () => { mo.disconnect(); if (timer !== null) window.clearTimeout(timer); };
  }, []);

  useEffect(() => {
    const zones = Array.from(
      document.querySelectorAll<HTMLElement>("[data-feedback-hide-zone]"),
    );
    const mql = window.matchMedia(MOBILE_MQ);
    // Seed synchronously so we don't flash the tab on first paint while
    // the IO callback is still pending.
    const isVisible = (el: HTMLElement) => {
      const r = el.getBoundingClientRect();
      return r.bottom > 0 && r.top < window.innerHeight;
    };
    const intersectingSet = new Set<Element>(zones.filter(isVisible));
    const sync = () => setHideOverHero(mql.matches && intersectingSet.size > 0);
    sync();

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) intersectingSet.add(entry.target);
          else intersectingSet.delete(entry.target);
        }
        sync();
      },
      { threshold: 0 },
    );
    zones.forEach((z) => io.observe(z));
    mql.addEventListener("change", sync);
    return () => {
      io.disconnect();
      mql.removeEventListener("change", sync);
    };
  }, [zoneEpoch]);

  // Hide on scroll down, show on scroll up
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const delta = y - lastY.current;
      lastY.current = y;
      let next = shownRef.current;
      if (y < 60)         next = true;
      else if (delta > 4)  next = false;
      else if (delta < -4) next = true;
      if (next !== shownRef.current) { shownRef.current = next; setShown(next); }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isOpen = widgetState !== "idle";

  const handleClose = useCallback(() => {
    setWidgetState("idle");
    setHasMessage(false);
    triggerRef.current?.focus();
    // Reset after exit animation
    setTimeout(() => {
      setCategory("General");
    }, 200);
  }, []);

  // Focus first field on open; Escape closes
  useEffect(() => {
    if (widgetState === "open") firstFieldRef.current?.focus();
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [widgetState, isOpen, handleClose]);

  const handleFocusTrapKeyDown = useFocusTrap(dialogRef, isOpen);

  // Anonymous visitors never see the feedback trigger.
  if (!email) return null;

  return (
    <>
      {/* ── Floating tab button ── */}
      <button
        ref={triggerRef}
        onClick={() => setWidgetState("open")}
        aria-label={t("shareFeedbackAria")}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        className={[
          "fixed top-[70%] z-base",
          "rounded-l-md bg-navy/80 text-white shadow-e4 backdrop-blur-md",
          "transition-colors duration-300 ease-in-out hover:bg-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold",
          widgetState !== "idle" || !shown || hideOverHero ? "pointer-events-none" : "",
        ].join(" ")}
        style={{
          width: near && widgetState === "idle" && shown && !hideOverHero ? 43 : 34,
          height: 112, overflow: "hidden", paddingBottom: 4,
          right: 0,
          transition: reducedMotion ? "none" : "transform 300ms ease-in-out, width 300ms ease-in-out",
          transform: (() => {
            if (widgetState !== "idle" || !shown || hideOverHero) return "translate(100%, -50%)";
            if (near) return "translate(0, -50%)";
            return "translate(calc(45% - 4px), -50%)"; // peek — ~23px visible
          })(),
        }}
      >
        <span
          className="absolute flex items-center gap-[6px] whitespace-nowrap"
          style={{ top: "50%", left: 13, transform: "translate(-50%, -50%) rotate(-90deg)" }}
        >
          <Chats size={13} weight="fill" aria-hidden="true" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em]">
            {t("tabLabel")}
          </span>
        </span>
      </button>

      {/* ── Modal overlay ── */}
      {isOpen && (
        <div
          className="fixed inset-0 z-overlay flex items-center justify-center bg-navy/40 px-21 backdrop-blur-[2px]"
          role="presentation"
          onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="feedback-title"
            onKeyDown={handleFocusTrapKeyDown}
            className="w-full max-w-[420px] rounded-pop border border-card-line bg-white shadow-e5"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-navy/10 px-21 py-21">
              <div className="flex items-center gap-8">
                <Chats size={18} weight="fill" className="text-navy" aria-hidden="true" />
                <h2
                  id="feedback-title"
                  className="font-serif text-[1.2rem] leading-tight text-navy"
                >
                  {t("title")}
                </h2>
              </div>
              <button
                onClick={handleClose}
                aria-label={t("closeAria")}
                className="rounded-control p-[4px] text-gray-600 transition-colors hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>

            {widgetState === "success" ? (
              /* ── Success state ── */
              <div className="flex flex-col items-center px-21 py-55 text-center">
                <CheckCircle
                  size={40}
                  weight="fill"
                  /* Unified success-icon green (2026-08-26) — see Toast. */
                  className="text-green-strong"
                  aria-hidden="true"
                />
                <h3 className="mt-13 font-serif text-xl text-navy">
                  {t("thankYou")}
                </h3>
                <p className="mt-8 max-w-[280px] text-sm leading-relaxed text-gray-600">
                  {t("successBody")}
                </p>
                <button
                  onClick={handleClose}
                  className="mt-21 rounded-control bg-navy px-21 py-[10px] text-xs font-semibold uppercase tracking-[0.06em] text-white transition-colors hover:bg-navy/85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold"
                >
                  {t("close")}
                </button>
              </div>
            ) : (
              /* ── Form ── */
              <form action={formAction}>
                <div className="space-y-21 px-21 py-21">

                  {/* Server-side error */}
                  {actionState?.status === "error" && (
                    <p role="alert" className="rounded-control bg-red-50 px-13 py-8 text-xs text-red-700">
                      {actionState.message}
                    </p>
                  )}

                  {/* Category */}
                  <fieldset>
                    <legend className="mb-8 text-[11px] font-semibold uppercase tracking-widest text-navy">
                      {t("typeLegend")}
                    </legend>
                    <div className="flex flex-wrap gap-8" role="group">
                      {CATEGORIES.map((cat) => (
                        <label
                          key={cat}
                          className={[
                            "flex cursor-pointer items-center gap-[6px] rounded-full border px-13 py-[6px] text-[11px] font-semibold uppercase tracking-[0.07em] transition-colors",
                            category === cat
                              ? "border-navy bg-navy text-white"
                              : "border-navy/20 text-navy hover:border-navy/50",
                          ].join(" ")}
                        >
                          <input
                            type="radio"
                            name="category"
                            value={cat}
                            checked={category === cat}
                            onChange={() => setCategory(cat)}
                            className="sr-only"
                          />
                          {t(`cat${cat}`)}
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  {/* Message */}
                  <div>
                    <label
                      htmlFor="fb-message"
                      className="mb-8 block text-[11px] font-semibold uppercase tracking-widest text-navy"
                    >
                      {t("messageLabel")} <span className="text-navy" aria-hidden="true">*</span>
                    </label>
                    <textarea
                      id="fb-message"
                      ref={firstFieldRef}
                      name="message"
                      required
                      rows={4}
                      onChange={(e) => setHasMessage(e.target.value.trim().length > 0)}
                      placeholder={t("messagePlaceholder")}
                      className="w-full resize-y rounded-control border border-navy/20 bg-white px-13 py-[10px] text-sm text-navy placeholder:text-gray-600 focus:border-navy/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label
                      htmlFor="fb-email"
                      className="mb-8 block text-[11px] font-semibold uppercase tracking-widest text-navy"
                    >
                      {t("emailLabel")} <span className="text-navy" aria-hidden="true">*</span>
                    </label>
                    <input
                      id="fb-email"
                      type="email"
                      name="email"
                      required
                      defaultValue={email}
                      readOnly
                      className="w-full rounded-control border border-navy/10 bg-navy/5 px-13 py-[10px] text-sm text-navy/60 cursor-default select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col-reverse gap-13 border-t border-navy/10 px-21 py-21 sm:flex-row sm:gap-8 sm:py-13">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="flex-1 rounded-control border border-navy/20 py-[10px] text-xs font-semibold uppercase tracking-[0.06em] text-navy transition-colors hover:border-navy/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold"
                  >
                    {t("cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={!hasMessage || isPending}
                    className="flex flex-1 items-center justify-center gap-8 rounded-control bg-gold py-13 text-xs font-semibold uppercase tracking-[0.06em] text-navy transition-colors hover:bg-gold-hover disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold sm:py-[10px]"
                  >
                    <PaperPlaneTilt size={13} weight="fill" aria-hidden="true" />
                    <BusyLabel busy={isPending}>{isPending ? t("sending") : t("send")}</BusyLabel>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
